(function () {
  'use strict';

  var config = window.PaganWishlistConfig || {};
  var STORAGE_KEY = 'pagan_workshop_wishlist_v1';
  var REMOVED_KEY = 'pagan_workshop_wishlist_removed_v1';
  var LAST_SYNC_KEY = 'pagan_workshop_wishlist_last_sync_v1';
  var TOKEN_KEY = 'pagan_workshop_customer_token_v1';
  var AUTH_STATE_KEY = 'pagan_workshop_wishlist_auth_state';
  var VERIFIER_KEY = 'pagan_workshop_wishlist_code_verifier';
  var RETURN_URL_KEY = 'pagan_workshop_wishlist_return_url';
  var SYNC_AFTER_AUTH_KEY = 'pagan_workshop_wishlist_sync_after_auth';
  var AUTH_MESSAGE_TYPE = 'pagan-workshop:wishlist-authenticated';
  var toastTimer;
  var apiDiscovery;
  var proxySyncQueue = Promise.resolve();

  var CUSTOMER_QUERY = [
    'query StorefrontWishlist {',
    '  customer {',
    '    id',
    '    wishlist: metafield(namespace: "$app", key: "wishlist") { jsonValue }',
    '  }',
    '}'
  ].join('\n');

  var SAVE_WISHLIST_MUTATION = [
    'mutation SaveStorefrontWishlist($metafields: [MetafieldsSetInput!]!) {',
    '  metafieldsSet(metafields: $metafields) {',
    '    metafields { id key jsonValue }',
    '    userErrors { field message code }',
    '  }',
    '}'
  ].join('\n');

  function safeParse(value, fallback) {
    try {
      return JSON.parse(value);
    } catch (error) {
      return fallback;
    }
  }

  function uniqueStrings(values) {
    return Array.from(new Set((Array.isArray(values) ? values : []).filter(function (value) {
      return typeof value === 'string' && value.length > 0;
    })));
  }

  function getItems() {
    var items = safeParse(localStorage.getItem(STORAGE_KEY), []);
    if (!Array.isArray(items)) return [];
    return items.filter(function (item) {
      return item && typeof item.id === 'string' && item.id.indexOf('gid://shopify/Product/') === 0;
    });
  }

  function setItems(items) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    document.dispatchEvent(new CustomEvent('pagan:wishlist:change', {detail: {items: items}}));
  }

  function getRemovedIds() {
    return uniqueStrings(safeParse(localStorage.getItem(REMOVED_KEY), []));
  }

  function setRemovedIds(ids) {
    localStorage.setItem(REMOVED_KEY, JSON.stringify(uniqueStrings(ids)));
  }

  function getLastSyncedIds() {
    return uniqueStrings(safeParse(localStorage.getItem(LAST_SYNC_KEY), []));
  }

  function setLastSyncedIds(ids) {
    localStorage.setItem(LAST_SYNC_KEY, JSON.stringify(uniqueStrings(ids)));
  }

  function productFromButton(button) {
    return {
      id: button.dataset.productId,
      handle: button.dataset.productHandle,
      title: button.dataset.productTitle,
      url: button.dataset.productUrl,
      image: button.dataset.productImage,
      price: button.dataset.productPrice,
      available: button.dataset.productAvailable === 'true'
    };
  }

  function hasProduct(productId) {
    return getItems().some(function (item) { return item.id === productId; });
  }

  function labelFor(template, title) {
    return String(template || '').replace('__TITLE__', title || '');
  }

  function updateButtons() {
    var ids = new Set(getItems().map(function (item) { return item.id; }));
    document.querySelectorAll('[data-wishlist-button]').forEach(function (button) {
      var active = ids.has(button.dataset.productId);
      var title = button.dataset.productTitle || '';
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      button.setAttribute('aria-label', labelFor(active ? config.labels.removeProduct : config.labels.addProduct, title));
      var text = button.querySelector('[data-wishlist-button-label]');
      if (text) text.textContent = active ? config.labels.remove : config.labels.add;
    });

    document.querySelectorAll('[data-wishlist-count]').forEach(function (count) {
      count.textContent = String(ids.size);
      count.hidden = ids.size === 0;
    });
  }

  function track(eventName, item) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      product_id: item.id,
      product_handle: item.handle,
      product_title: item.title
    });
  }

  function showToast(message) {
    var toast = document.querySelector('[data-wishlist-toast]');
    if (!toast) return;
    window.clearTimeout(toastTimer);
    toast.textContent = message;
    toast.hidden = false;
    toastTimer = window.setTimeout(function () { toast.hidden = true; }, 2600);
  }

  function toggleProduct(item) {
    var items = getItems();
    var exists = items.some(function (saved) { return saved.id === item.id; });
    var removed = getRemovedIds();

    var action;
    if (exists) {
      action = 'remove';
      items = items.filter(function (saved) { return saved.id !== item.id; });
      removed = uniqueStrings(removed.concat(item.id));
      showToast(config.labels.removed);
      track('wishlist_remove', item);
    } else {
      action = 'add';
      items.push(item);
      removed = removed.filter(function (id) { return id !== item.id; });
      showToast(config.labels.saved);
      track('wishlist_add', item);
    }

    setRemovedIds(removed);
    setItems(items);

    // A heart click must always remain on the current storefront page. Customer
    // Account OAuth can be promoted to a full-page navigation by mobile Safari
    // and in-app browsers even when it was requested as a popup. Only sync in
    // the background when this tab already owns a valid Customer Account token;
    // otherwise keep the change locally until the customer explicitly syncs it.
    if (canProxySync()) {
      syncWishlistProxy({action: action, productId: item.id}, {quiet: true}).catch(function () {
        updateSyncButton();
      });
    } else if (getToken()) {
      syncWishlist({quiet: true}).catch(function () {});
    } else {
      updateSyncButton();
    }
  }

  function canProxySync() {
    return Boolean(config.customerLoggedIn && config.proxyUrl);
  }

  function syncWishlistProxy(payload, options) {
    // Shopify app-proxy requests can finish out of order when a customer taps
    // several hearts quickly. Serialize them so a later write cannot restore
    // an item that an earlier click already removed.
    proxySyncQueue = proxySyncQueue.catch(function () {}).then(function () {
      return performWishlistProxySync(payload, options);
    });
    return proxySyncQueue;
  }

  async function performWishlistProxySync(payload, options) {
    options = options || {};
    if (!canProxySync()) return false;
    if (!options.quiet) setStatus(config.labels.syncing);

    var response = await fetch(config.proxyUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify(payload)
    });
    var result = await response.json().catch(function () { return {}; });
    if (!response.ok || !result.ok) {
      if (!options.quiet) setStatus(config.labels.syncError);
      throw new Error(result.error || 'Wishlist proxy sync failed');
    }

    setLastSyncedIds(uniqueStrings(result.wishlist));
    if (payload.action === 'remove' && payload.productId) {
      setRemovedIds(getRemovedIds().filter(function (id) { return id !== payload.productId; }));
    } else if (payload.action === 'sync') {
      setRemovedIds([]);
    }
    setStatus(config.labels.synced);
    updateSyncButton();
    return true;
  }

  function syncLocalWishlistProxy(options) {
    return syncWishlistProxy({
      action: 'sync',
      additions: getItems().map(function (item) { return item.id; }),
      removals: getRemovedIds()
    }, options);
  }

  function createElement(tag, className, text) {
    var element = document.createElement(tag);
    if (className) element.className = className;
    if (typeof text === 'string') element.textContent = text;
    return element;
  }

  function renderDrawer() {
    var items = getItems();
    var container = document.querySelector('[data-wishlist-items]');
    var empty = document.querySelector('[data-wishlist-empty]');
    if (!container || !empty) return;

    container.textContent = '';
    empty.hidden = items.length > 0;

    items.forEach(function (item) {
      var row = createElement('article', 'pw-wishlist__item');
      var link = createElement('a');
      link.href = item.url || ('/products/' + item.handle);

      if (item.image) {
        var image = createElement('img', 'pw-wishlist__item-image');
        image.src = item.image;
        image.alt = item.title || '';
        image.loading = 'lazy';
        link.appendChild(image);
      }

      var details = createElement('div');
      var title = createElement('a', 'pw-wishlist__item-title', item.title || item.handle);
      title.href = item.url || ('/products/' + item.handle);
      details.appendChild(title);
      if (item.price) details.appendChild(createElement('span', 'pw-wishlist__item-price', item.price));
      if (!item.available) details.appendChild(createElement('span', 'pw-wishlist__item-state', config.labels.soldOut));

      var remove = createElement('button', 'pw-wishlist__item-remove tap-area');
      remove.type = 'button';
      remove.dataset.wishlistRemove = item.id;
      remove.setAttribute('aria-label', config.labels.removeItem);
      remove.textContent = '×';

      row.appendChild(link);
      row.appendChild(details);
      row.appendChild(remove);
      container.appendChild(row);
    });

    updateSyncButton();
  }

  function setStatus(message) {
    var status = document.querySelector('[data-wishlist-status]');
    if (status) status.textContent = message || '';
  }

  function openDrawer() {
    var root = document.querySelector('[data-wishlist-root]');
    if (!root) return;
    renderDrawer();
    root.hidden = false;
    document.body.classList.add('pw-wishlist-open');
    var close = root.querySelector('[data-wishlist-close]');
    if (close) close.focus();
  }

  function closeDrawer() {
    var root = document.querySelector('[data-wishlist-root]');
    if (root) root.hidden = true;
    document.body.classList.remove('pw-wishlist-open');
  }

  function randomBase64Url(length) {
    var bytes = new Uint8Array(length);
    window.crypto.getRandomValues(bytes);
    return base64Url(bytes);
  }

  function base64Url(bytes) {
    var binary = '';
    bytes.forEach(function (byte) { binary += String.fromCharCode(byte); });
    return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  async function sha256(value) {
    var data = new TextEncoder().encode(value);
    return new Uint8Array(await window.crypto.subtle.digest('SHA-256', data));
  }

  async function discoverAuthentication() {
    var response = await fetch(config.storefrontOrigin + '/.well-known/openid-configuration', {
      credentials: 'same-origin'
    });
    if (!response.ok) throw new Error('Authentication discovery failed');
    return response.json();
  }

  async function discoverCustomerApi() {
    if (apiDiscovery) return apiDiscovery;
    var response = await fetch(config.storefrontOrigin + '/.well-known/customer-account-api', {
      credentials: 'same-origin'
    });
    if (!response.ok) throw new Error('Customer API discovery failed');
    apiDiscovery = await response.json();
    return apiDiscovery;
  }

  function getToken() {
    var token = safeParse(sessionStorage.getItem(TOKEN_KEY), null);
    if (!token || !token.accessToken || !token.expiresAt || Date.now() >= token.expiresAt) {
      sessionStorage.removeItem(TOKEN_KEY);
      return null;
    }
    return token.accessToken;
  }

  function saveToken(payload) {
    var expiresIn = Number(payload.expires_in || 3600);
    var token = {
      accessToken: payload.access_token,
      expiresAt: Date.now() + Math.max(60, expiresIn - 60) * 1000
    };
    sessionStorage.setItem(TOKEN_KEY, JSON.stringify(token));
    return token;
  }

  async function beginAuthentication(options) {
    options = options || {};
    if (!window.crypto || !window.crypto.subtle) throw new Error('Secure authentication is unavailable');
    setStatus(config.labels.syncing);
    var verifier = randomBase64Url(48);
    var state = 'pw_wishlist_' + randomBase64Url(24);

    sessionStorage.setItem(AUTH_STATE_KEY, state);
    sessionStorage.setItem(VERIFIER_KEY, verifier);
    sessionStorage.setItem(RETURN_URL_KEY, window.location.href);
    sessionStorage.setItem(SYNC_AFTER_AUTH_KEY, '1');

    // Open synchronously from the Wishlist click before awaiting discovery or
    // hashing. This keeps mobile Safari and other popup blockers from treating
    // the authentication window as unsolicited.
    var authWindow = null;
    if (options.popup) {
      authWindow = window.open('about:blank', 'paganWishlistAuthentication', 'popup=yes,width=520,height=720');
      if (!authWindow) throw new Error('Authentication popup was blocked');
    }

    var discovery;
    var challenge;
    try {
      discovery = await discoverAuthentication();
      challenge = base64Url(await sha256(verifier));
    } catch (error) {
      if (authWindow) authWindow.close();
      throw error;
    }

    var authorizationUrl = new URL(discovery.authorization_endpoint);
    authorizationUrl.searchParams.set('client_id', config.clientId);
    authorizationUrl.searchParams.set('response_type', 'code');
    authorizationUrl.searchParams.set('redirect_uri', config.redirectUri);
    authorizationUrl.searchParams.set('scope', 'openid email customer-account-api:full');
    authorizationUrl.searchParams.set('state', state);
    authorizationUrl.searchParams.set('code_challenge', challenge);
    authorizationUrl.searchParams.set('code_challenge_method', 'S256');
    if (authWindow) {
      authWindow.location.replace(authorizationUrl.toString());
    } else {
      window.location.assign(authorizationUrl.toString());
    }
  }

  async function handleAuthenticationCallback() {
    var params = new URLSearchParams(window.location.search);
    var code = params.get('code');
    var state = params.get('state');
    if (!code || !state || state.indexOf('pw_wishlist_') !== 0) return false;

    var expectedState = sessionStorage.getItem(AUTH_STATE_KEY);
    var verifier = sessionStorage.getItem(VERIFIER_KEY);
    if (!expectedState || state !== expectedState || !verifier) throw new Error('Invalid authentication state');

    var discovery = await discoverAuthentication();
    var response = await fetch(discovery.token_endpoint, {
      method: 'POST',
      headers: {'Content-Type': 'application/x-www-form-urlencoded'},
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: config.clientId,
        redirect_uri: config.redirectUri,
        code: code,
        code_verifier: verifier
      }).toString()
    });
    var payload = await response.json();
    if (!response.ok || !payload.access_token) throw new Error(payload.error_description || 'Token exchange failed');
    var savedToken = saveToken(payload);

    sessionStorage.removeItem(AUTH_STATE_KEY);
    sessionStorage.removeItem(VERIFIER_KEY);

    if (window.opener && !window.opener.closed) {
      window.opener.postMessage({
        type: AUTH_MESSAGE_TYPE,
        token: savedToken
      }, config.storefrontOrigin);
      window.close();
      return true;
    }

    var returnUrl = sessionStorage.getItem(RETURN_URL_KEY) || config.storefrontOrigin;
    sessionStorage.removeItem(RETURN_URL_KEY);
    window.location.replace(returnUrl);
    return true;
  }

  async function customerGraphql(query, variables) {
    var token = getToken();
    if (!token) throw new Error('AUTH_REQUIRED');
    var discovery = await discoverCustomerApi();
    var endpoint = discovery.graphql_api || discovery.graphql_api_url || discovery.graphqlApi;
    if (!endpoint) throw new Error('Customer GraphQL endpoint missing');

    var response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token
      },
      body: JSON.stringify({query: query, variables: variables || {}})
    });
    if (response.status === 401) {
      sessionStorage.removeItem(TOKEN_KEY);
      throw new Error('AUTH_REQUIRED');
    }
    var payload = await response.json();
    if (!response.ok || (payload.errors && payload.errors.length)) {
      throw new Error((payload.errors && payload.errors[0] && payload.errors[0].message) || 'Customer API request failed');
    }
    return payload.data;
  }

  async function syncWishlist(options) {
    options = options || {};
    if (canProxySync()) return syncLocalWishlistProxy(options);
    if (!getToken()) {
      if (options.interactive) return beginAuthentication({popup: true});
      updateSyncButton();
      return false;
    }

    if (!options.quiet) setStatus(config.labels.syncing);
    try {
      var data = await customerGraphql(CUSTOMER_QUERY);
      var customer = data.customer;
      if (!customer || !customer.id) throw new Error('Customer not found');
      var serverIds = uniqueStrings(customer.wishlist && customer.wishlist.jsonValue);
      var localIds = uniqueStrings(getItems().map(function (item) { return item.id; }));
      var lastSyncedIds = getLastSyncedIds();
      var additions = lastSyncedIds.length
        ? localIds.filter(function (id) { return !lastSyncedIds.includes(id); })
        : localIds;
      var removals = uniqueStrings(
        getRemovedIds().concat(
          lastSyncedIds.filter(function (id) { return !localIds.includes(id); })
        )
      );
      var removed = new Set(removals);
      var merged = uniqueStrings(serverIds.concat(additions)).filter(function (id) { return !removed.has(id); });

      var result = await customerGraphql(SAVE_WISHLIST_MUTATION, {
        metafields: [{
          ownerId: customer.id,
          key: 'wishlist',
          type: 'list.product_reference',
          value: JSON.stringify(merged)
        }]
      });
      var errors = (result.metafieldsSet && result.metafieldsSet.userErrors) || [];
      if (errors.length) throw new Error(errors[0].message);
      var retainedLocalItems = getItems().filter(function (item) { return merged.includes(item.id); });
      setRemovedIds([]);
      setLastSyncedIds(retainedLocalItems.map(function (item) { return item.id; }));
      setItems(retainedLocalItems);
      setStatus(config.labels.synced);
      updateSyncButton();
      return true;
    } catch (error) {
      if (error.message === 'AUTH_REQUIRED') {
        updateSyncButton();
        if (options.interactive) return beginAuthentication({popup: true});
      }
      if (!options.quiet) setStatus(config.labels.syncError);
      throw error;
    }
  }

  function updateSyncButton() {
    var button = document.querySelector('[data-wishlist-sync]');
    if (!button) return;
    button.textContent = (canProxySync() || getToken()) ? config.labels.sync : config.labels.signIn;
    if (!canProxySync() && !getToken()) setStatus(config.labels.localStatus);
  }

  function handleAuthenticationMessage(event) {
    if (event.origin !== config.storefrontOrigin) return;
    if (!event.data || event.data.type !== AUTH_MESSAGE_TYPE) return;
    var token = event.data.token;
    if (!token || typeof token.accessToken !== 'string' || !token.expiresAt) return;

    sessionStorage.setItem(TOKEN_KEY, JSON.stringify(token));
    sessionStorage.removeItem(AUTH_STATE_KEY);
    sessionStorage.removeItem(VERIFIER_KEY);
    sessionStorage.removeItem(RETURN_URL_KEY);
    sessionStorage.removeItem(SYNC_AFTER_AUTH_KEY);

    syncWishlist({quiet: true}).then(function () {
      showToast(config.labels.synced);
    }).catch(function () {
      setStatus(config.labels.syncError);
    });
  }

  function bindEvents() {
    window.addEventListener('message', handleAuthenticationMessage);

    // Handle Wishlist hearts during capture so product-card links, sliders and
    // theme click handlers never receive the same tap and cannot navigate away.
    document.addEventListener('click', function (event) {
      var button = event.target.closest('[data-wishlist-button]');
      if (!button) return;

      event.preventDefault();
      event.stopPropagation();
      toggleProduct(productFromButton(button));
    }, true);

    document.addEventListener('click', function (event) {
      if (event.target.closest('[data-wishlist-open]')) {
        event.preventDefault();
        openDrawer();
        return;
      }

      if (event.target.closest('[data-wishlist-close]')) {
        event.preventDefault();
        closeDrawer();
        return;
      }

      var remove = event.target.closest('[data-wishlist-remove]');
      if (remove) {
        var item = getItems().find(function (saved) { return saved.id === remove.dataset.wishlistRemove; });
        if (item) toggleProduct(item);
        renderDrawer();
        return;
      }

      if (event.target.closest('[data-wishlist-sync]')) {
        event.preventDefault();
        syncWishlist({interactive: true}).catch(function () { setStatus(config.labels.syncError); });
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') closeDrawer();
    });

    document.addEventListener('pagan:wishlist:change', function () {
      updateButtons();
      renderDrawer();
    });
  }

  async function init() {
    try {
      var redirecting = await handleAuthenticationCallback();
      if (redirecting) return;
    } catch (error) {
      console.error('Wishlist authentication failed', error);
      setStatus(config.labels.syncError);
    }

    bindEvents();
    updateButtons();
    renderDrawer();

    if (canProxySync()) {
      syncLocalWishlistProxy({quiet: true}).catch(function () {
        updateSyncButton();
      });
    }

    if (sessionStorage.getItem(SYNC_AFTER_AUTH_KEY) === '1' && getToken()) {
      sessionStorage.removeItem(SYNC_AFTER_AUTH_KEY);
      syncWishlist({quiet: true}).then(function () {
        showToast(config.labels.synced);
      }).catch(function () {});
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
